import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, FileText, Calendar, Download, AlertTriangle, Eye, EyeOff, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Layers, List } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import * as pdfjsLib from 'pdfjs-dist/webpack';
import useScreenCaptureDetection from '../hooks/useScreenCaptureDetection';

const PDFViewer = () => {
  const { fileId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [fileInfo, setFileInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pdfDocument, setPdfDocument] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.2);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [debugInfo, setDebugInfo] = useState([]);
  const [viewMode, setViewMode] = useState('auto'); // 'auto', 'pagination', 'continuous'
  const [renderedPages, setRenderedPages] = useState(new Map());
  const canvasRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // Initialize screen capture detection
  const { reportSuspiciousActivity, showWarning } = useScreenCaptureDetection(
    true, // isActive
    fileInfo?.name // fileName
  );

  const addDebugInfo = (message) => {
    console.log('DEBUG:', message);
    // Only store debug info for admin users
    if (user?.role === 'admin') {
      setDebugInfo(prev => [...prev.slice(-10), `${new Date().toLocaleTimeString()}: ${message}`]);
    }
  };

  // Determine optimal view mode based on page count
  const getEffectiveViewMode = () => {
    if (viewMode === 'auto') {
      return totalPages > 10 ? 'continuous' : 'pagination';
    }
    return viewMode;
  };

  useEffect(() => {
    if (fileId) {
      addDebugInfo('Starting PDF fetch process');
      fetchFileInfo();
      fetchAndRenderPDF();
    }
  }, [fileId]);

  useEffect(() => {
    const effectiveMode = getEffectiveViewMode();
    if (pdfDocument && totalPages > 0) {
      if (effectiveMode === 'pagination' && currentPage && canvasRef.current) {
        addDebugInfo(`Rendering page ${currentPage} with scale ${scale}`);
        renderSinglePage(currentPage);
      } else if (effectiveMode === 'continuous') {
        addDebugInfo(`Rendering all ${totalPages} pages in continuous mode`);
        renderAllPages();
      }
    }
  }, [pdfDocument, currentPage, scale, totalPages, viewMode]);

  const fetchFileInfo = async () => {
    try {
      setError('');
      addDebugInfo('Fetching file info...');
      const response = await api.get(`/pdf/info/${fileId}`);
      setFileInfo(response.data);
      addDebugInfo(`File info loaded: ${response.data.name}`);
    } catch (error) {
      console.error('Error fetching file info:', error);
      addDebugInfo(`Error fetching file info: ${error.message}`);
      setError('Hiba történt a fájl információinak betöltése során.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAndRenderPDF = async () => {
    try {
      setPdfLoading(true);
      addDebugInfo('Setting up PDF.js worker...');
      
      // Configure PDF.js worker with a stable version
      pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      addDebugInfo('Worker configured');
      
      addDebugInfo('Fetching PDF data...');
      const response = await api.get(`/pdf/view/${fileId}`, {
        responseType: 'arraybuffer'
      });
      
      addDebugInfo(`PDF data received: ${response.data.byteLength} bytes`);
      
      const loadingTask = pdfjsLib.getDocument({
        data: response.data,
        verbosity: pdfjsLib.VerbosityLevel.ERRORS
      });
      
      addDebugInfo('Loading PDF document...');
      const pdfDoc = await loadingTask.promise;
      addDebugInfo(`PDF loaded successfully: ${pdfDoc.numPages} pages`);
      
      setPdfDocument(pdfDoc);
      setTotalPages(pdfDoc.numPages);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error fetching PDF:', error);
      addDebugInfo(`Error loading PDF: ${error.message}`);
      setError('Hiba történt a PDF betöltése során: ' + error.message);
    } finally {
      setPdfLoading(false);
    }
  };

  const renderSinglePage = async (pageNumber) => {
    if (!pdfDocument || !canvasRef.current) {
      addDebugInfo('Cannot render: no PDF document or canvas');
      return;
    }
    
    try {
      addDebugInfo(`Starting to render page ${pageNumber}`);
      const page = await pdfDocument.getPage(pageNumber);
      addDebugInfo('Page object obtained');
      
      const viewport = page.getViewport({ scale });
      addDebugInfo(`Viewport: ${viewport.width}x${viewport.height}`);
      
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      // Set canvas size
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      
      addDebugInfo(`Canvas size set: ${canvas.width}x${canvas.height}`);
      
      // Clear canvas with white background
      context.fillStyle = 'white';
      context.fillRect(0, 0, canvas.width, canvas.height);
      
      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };
      
      addDebugInfo('Starting render task...');
      const renderTask = page.render(renderContext);
      
      await renderTask.promise;
      addDebugInfo('Page rendered successfully!');
      
    } catch (error) {
      console.error('Error rendering page:', error);
      addDebugInfo(`Render error: ${error.message}`);
      setError('Hiba történt az oldal renderelése során: ' + error.message);
    }
  };

  const renderAllPages = async () => {
    if (!pdfDocument || !scrollContainerRef.current) {
      addDebugInfo('Cannot render continuous: no PDF document or scroll container');
      return;
    }

    try {
      addDebugInfo('Starting continuous page rendering...');
      const container = scrollContainerRef.current;
      
      // Clear container
      container.innerHTML = '';
      
      const newRenderedPages = new Map();
      
      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        const page = await pdfDocument.getPage(pageNum);
        const viewport = page.getViewport({ scale });
        
        // Create canvas for this page
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        canvas.className = 'block mx-auto mb-4 shadow-lg border-2 border-gray-300 bg-white';
        canvas.style.userSelect = 'none';
        canvas.style.WebkitUserSelect = 'none';
        canvas.style.MozUserSelect = 'none';
        canvas.style.msUserSelect = 'none';
        canvas.style.maxWidth = '100%';
        canvas.style.height = 'auto';
        
        // Disable context menu
        canvas.oncontextmenu = (e) => e.preventDefault();
        
        // Clear canvas with white background
        context.fillStyle = 'white';
        context.fillRect(0, 0, canvas.width, canvas.height);
        
        const renderContext = {
          canvasContext: context,
          viewport: viewport
        };
        
        await page.render(renderContext).promise;
        
        // Create page wrapper with page number
        const pageWrapper = document.createElement('div');
        pageWrapper.className = 'relative';
        pageWrapper.id = `page-${pageNum}`;
        
        const pageLabel = document.createElement('div');
        pageLabel.className = 'absolute top-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded z-10';
        pageLabel.textContent = `${pageNum} / ${totalPages}`;
        
        pageWrapper.appendChild(pageLabel);
        pageWrapper.appendChild(canvas);
        container.appendChild(pageWrapper);
        
        newRenderedPages.set(pageNum, canvas);
        
        addDebugInfo(`Rendered page ${pageNum}/${totalPages}`);
      }
      
      setRenderedPages(newRenderedPages);
      addDebugInfo('All pages rendered successfully in continuous mode');
      
    } catch (error) {
      console.error('Error rendering continuous pages:', error);
      addDebugInfo(`Continuous render error: ${error.message}`);
      setError('Hiba történt a folyamatos megjelenítés során: ' + error.message);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const scrollToPage = (pageNum) => {
    if (scrollContainerRef.current) {
      const pageElement = document.getElementById(`page-${pageNum}`);
      if (pageElement) {
        pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setCurrentPage(pageNum);
      }
    }
  };

  const zoomIn = () => {
    setScale(prevScale => Math.min(prevScale + 0.2, 3.0));
  };

  const zoomOut = () => {
    setScale(prevScale => Math.max(prevScale - 0.2, 0.5));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('hu-HU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const effectiveViewMode = getEffectiveViewMode();

  if (loading || pdfLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">
            {loading ? 'Fájl információk betöltése...' : 'PDF betöltése...'}
          </p>
          {user?.role === 'admin' && debugInfo.length > 0 && (
            <div className="mt-4 text-xs text-left bg-gray-100 p-4 rounded max-w-md">
              <strong>Debug (Admin):</strong>
              {debugInfo.map((info, idx) => (
                <div key={idx}>{info}</div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (error || !fileInfo) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="card p-8 text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Hiba történt</h2>
          <p className="text-gray-600 mb-6">
            {error || 'A kért PDF fájl nem található vagy nem elérhető.'}
          </p>
          {user?.role === 'admin' && debugInfo.length > 0 && (
            <div className="text-xs text-left bg-gray-100 p-4 rounded mb-4">
              <strong>Debug információk (Admin):</strong>
              {debugInfo.map((info, idx) => (
                <div key={idx}>{info}</div>
              ))}
            </div>
          )}
          <Link
            to="/dashboard"
            className="btn-primary inline-flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Vissza a dokumentumokhoz</span>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-4">
          <Link
            to="/dashboard"
            className="btn-secondary inline-flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Vissza</span>
          </Link>
          
          <div>
            <h1 className="text-2xl font-bold text-gray-900 truncate" title={fileInfo.name}>
              {fileInfo.name}
            </h1>
            <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
              <div className="flex items-center space-x-1">
                <Download className="h-4 w-4" />
                <span>{formatFileSize(fileInfo.size)}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Calendar className="h-4 w-4" />
                <span>{formatDate(fileInfo.modified)}</span>
              </div>
              <div className="flex items-center space-x-1">
                <FileText className="h-4 w-4" />
                <span>{totalPages} oldal</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Security Notice */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <div className="flex items-center space-x-2 text-sm text-yellow-800">
            <EyeOff className="h-4 w-4" />
            <span>Csak megtekintésre - letöltés nem engedélyezett</span>
          </div>
        </div>
      </div>

      {/* Debug Panel - Only for admin users */}
      {user?.role === 'admin' && debugInfo.length > 0 && (
        <div className="mb-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Debug információk (Admin):</h3>
          <div className="text-xs text-gray-600 space-y-1 max-h-32 overflow-y-auto">
            {debugInfo.map((info, idx) => (
              <div key={idx}>{info}</div>
            ))}
          </div>
        </div>
      )}

      {/* PDF Controls */}
      {pdfDocument && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center space-x-4">
              {/* View Mode Selector */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">Megjelenítés:</span>
                <div className="flex rounded-md shadow-sm">
                  <button
                    onClick={() => setViewMode('pagination')}
                    className={`px-3 py-1 text-xs font-medium rounded-l-md border ${
                      effectiveViewMode === 'pagination'
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                    title="Lapozgatós nézet"
                  >
                    <List className="h-3 w-3" />
                  </button>
                  <button
                    onClick={() => setViewMode('continuous')}
                    className={`px-3 py-1 text-xs font-medium rounded-r-md border-t border-r border-b ${
                      effectiveViewMode === 'continuous'
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                    title="Folyamatos görgetés"
                  >
                    <Layers className="h-3 w-3" />
                  </button>
                </div>
                {viewMode === 'auto' && (
                  <span className="text-xs text-gray-500">
                    (Auto: {effectiveViewMode === 'continuous' ? 'Görgetős' : 'Lapozós'})
                  </span>
                )}
              </div>

              {/* Navigation Controls */}
              {effectiveViewMode === 'pagination' && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={goToPreviousPage}
                    disabled={currentPage <= 1}
                    className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Előző oldal"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-700 min-w-0">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={goToNextPage}
                    disabled={currentPage >= totalPages}
                    className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Következő oldal"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
              
              {/* Zoom Controls */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={zoomOut}
                  disabled={scale <= 0.5}
                  className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Kicsinyítés"
                >
                  <ZoomOut className="h-4 w-4" />
                </button>
                <span className="text-sm text-gray-700 min-w-0">
                  {Math.round(scale * 100)}%
                </span>
                <button
                  onClick={zoomIn}
                  disabled={scale >= 3.0}
                  className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Nagyítás"
                >
                  <ZoomIn className="h-4 w-4" />
                </button>
              </div>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <div className="flex items-center space-x-2 text-sm text-red-800">
                <EyeOff className="h-4 w-4" />
                <span>Letöltés és nyomtatás tiltva</span>
              </div>
            </div>
          </div>

          {/* Page Jump for Continuous Mode */}
          {effectiveViewMode === 'continuous' && totalPages > 5 && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">Ugrás oldalra:</span>
                <select
                  value={currentPage}
                  onChange={(e) => scrollToPage(parseInt(e.target.value))}
                  className="text-sm border border-gray-300 rounded px-2 py-1"
                >
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                    <option key={pageNum} value={pageNum}>
                      {pageNum}. oldal
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      {/* PDF Viewer */}
      <div className="card p-0 overflow-hidden">
        <div className="w-full" style={{ minHeight: '70vh' }}>
          {pdfDocument ? (
            effectiveViewMode === 'pagination' ? (
              // Pagination Mode
              <div className="flex justify-center p-4 bg-gray-100 min-h-full pdf-container">
                <div className="bg-white shadow-lg border-2 border-gray-300">
                  <canvas
                    ref={canvasRef}
                    className="block"
                    onContextMenu={(e) => e.preventDefault()}
                    style={{
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      MozUserSelect: 'none',
                      msUserSelect: 'none',
                      maxWidth: '100%',
                      height: 'auto'
                    }}
                  />
                </div>
              </div>
            ) : (
              // Continuous Scroll Mode
              <div 
                ref={scrollContainerRef}
                className="p-4 bg-gray-100 min-h-full pdf-container max-h-screen overflow-y-auto"
                style={{ maxHeight: '80vh' }}
              >
                {/* Content will be dynamically added by renderAllPages */}
              </div>
            )
          ) : (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                <p className="text-gray-600">PDF betöltése folyamatban...</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer Notice */}
      <div className="mt-4 text-center text-xs text-gray-500">
        <p>Ez a dokumentum csak megtekintésre szolgál. Minden jog fenntartva.</p>
        <p>© 2024 Wellum Data - Biztonságos dokumentum megtekintő</p>
      </div>
    </div>
  );
};

export default PDFViewer; 